import { mqttService } from '../../services/mqttService';
import { AmplifierData, AmplifierControlData, AmplifierProjectData, ProgramStatusMessage, HamlibControlRequestMessage, ProgramStatusResponseMessage, HamlibControlResponseMessage, UpdateAlarmLogRequestMessage, UpdateAlarmLogStatusMessage, DeviceUpdateCommandMessage } from './amplifier';

type AmplifierStatusSubscriber = {
    callback: (data: AmplifierData) => void;
    programStatusCallback?: (data: ProgramStatusMessage) => void;
    hamlibControlCallback?: (data: HamlibControlRequestMessage) => void;
    auditLogCallback?: (data: UpdateAlarmLogStatusMessage) => void;
};

type PendingByDevice = {
    pendingAmplifierData: AmplifierData | null;
    pendingProgramStatus: ProgramStatusMessage | null;
    pendingHamlib: HamlibControlRequestMessage | null;
    pendingAuditLog: UpdateAlarmLogStatusMessage | null;
    throttleTimerId: ReturnType<typeof setTimeout> | null;
};



/**
 * AmplifierControl MQTT服务
 * 专门用于功放控制组件的MQTT通信
 */
class AmplifierMqttService {
    private paramCommandIntervalId: NodeJS.Timeout | null = null;

    /**
     * 发送前确保 MQTT 已就绪：未连接时主动 connect()，避免点击后“等一会才发”或直接抛错。
     * 失败时仍会抛出，便于上层提示用户。
     */
    private async ensureMqttReady(label: string): Promise<void> {
        if (!mqttService.isConnected()) {
            try {
                await mqttService.connect();
            } catch (err) {
                console.error(`AmplifierMqttService: MQTT连接失败，无法发送${label}:`, err);
                throw new Error('MQTT服务未连接');
            }
        }
        if (!mqttService.isServiceInitialized()) {
            console.error(`AmplifierMqttService: MQTT服务未初始化，无法发送${label}`);
            throw new Error('MQTT服务未初始化');
        }
    }

    /**
     * 启动参数命令定时任务（已禁用）
     * 前端不再定时发送 synCommand，仅保留接口以兼容现有调用；如有需要由后端或其他方式下发
     */
    startParamCommandInterval(_deviceId: string, _userId: number): void {
        this.stopParamCommandInterval();
    }
    /**
     * 发送json_command指令
     * server/device/{deviceId}/json_command
     * 参数分别是HamlibControlResponseMessage和ProgramStatusResponseMessage
     */
    async sendJsonCommand(
        deviceId: string,
        message:
            | HamlibControlResponseMessage
            | ProgramStatusResponseMessage
            | UpdateAlarmLogRequestMessage
            | DeviceUpdateCommandMessage,
    ): Promise<void> {
        try {
            await this.ensureMqttReady('json_command');

            const mqttManager = mqttService.getMqttManager();

            // 主题格式：server/device/{deviceId}/json_command
            const topic = `server/device/${deviceId}/json_command`;
            const raw = message as unknown as Record<string, unknown>;
            const { type: _legacyType, ...payload } = raw;
            await mqttManager.publishJson(topic, payload, 1, false);
            console.log('AmplifierMqttService: json_command指令发送成功:', payload);
        } catch (error) {
            console.error('AmplifierMqttService: json_command指令发送失败:', error);
            throw error;
        }
    }


    /**
     * 发送功放设置指令
     * 功放设置指令格式：{"action":"updateAmplifierData","data":{amplifierData}}
     * 其中amplifierData包含triggerMode、tuneMode、power、frequency、inputAttenuator、inputAntenna、outputAntenna、alarmWord等参数
     * 其中tuneMode为0表示自动调频，1表示手动调频
     * 其中power为0表示关闭，1表示开启
     * 其中inputAttenuator为0表示关闭，1表示开启
     * 当用户在页面上操作按钮修改了amplifierData后，调用此方法发送功放设置指令
     */
    async sendAmpConfigCommand(deviceId: string, controlData: AmplifierControlData): Promise<void> {
        try {
            await this.ensureMqttReady('放大器数据更新');

            const mqttManager = mqttService.getMqttManager();

            // 构建消息对象，包含控制数据
            const message = {
                action: 'ampConfigCommand',
                ...controlData
            };

            // 主题格式：server/device/{deviceId}/command
            const topic = `server/device/${deviceId}/command`;
            await mqttManager.publishJson(topic, message, 1, false);
            console.log('AmplifierMqttService: 功放设置指令发送成功:', message);
        } catch (error) {
            console.error('AmplifierMqttService: 功放设置指令发送失败:', error);
            throw error;
        }
    }
    /**
     *发送工程参数指令
     *
     */
    async sendProjectParametersCommand(deviceId: string, projectData: AmplifierProjectData): Promise<void> {
        try {
            await this.ensureMqttReady('工程参数指令');

            const message = {
                command: 'projectParameters',
                ...projectData
            };
            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/command`;
            await mqttManager.publishJson(topic, message, 1, false);
            console.log('AmplifierMqttService: 工程参数指令发送成功:', message);
        } catch (error) {
            console.error('AmplifierMqttService: 工程参数指令发送失败:', error);
            throw error;
        }
    }


    /**
     * 停止参数命令定时任务
     */
    stopParamCommandInterval(): void {
        if (this.paramCommandIntervalId) {
            clearInterval(this.paramCommandIntervalId);
            this.paramCommandIntervalId = null;
            console.log('AmplifierMqttService: 参数命令定时任务已停止');
        }
    }


    /** 状态回调节流间隔（ms），降低 setState 频率以提升渲染效率，建议 80～150 */
    private static readonly STATUS_THROTTLE_MS = 100;
    /** 设备 -> 多订阅者（卡片/详情并存） */
    private subscribersByDevice = new Map<string, Map<string, AmplifierStatusSubscriber>>();
    /** 设备 -> 待下发消息（按设备隔离节流） */
    private pendingByDevice = new Map<string, PendingByDevice>();
    /** addMessageListener 取消函数；勿用 setCallbacks.onMessage，会覆盖 MqttAudioManager */
    private messageListenerUnsub: (() => void) | null = null;
    private subscriberCounter = 0;

    private getDevicePending(deviceId: string): PendingByDevice {
        const current = this.pendingByDevice.get(deviceId);
        if (current) return current;
        const created: PendingByDevice = {
            pendingAmplifierData: null,
            pendingProgramStatus: null,
            pendingHamlib: null,
            pendingAuditLog: null,
            throttleTimerId: null,
        };
        this.pendingByDevice.set(deviceId, created);
        return created;
    }

    private flushDevice(deviceId: string): void {
        const pending = this.pendingByDevice.get(deviceId);
        if (!pending) return;
        pending.throttleTimerId = null;
        const subscribers = this.subscribersByDevice.get(deviceId);
        if (!subscribers || subscribers.size === 0) {
            this.clearThrottle(deviceId);
            return;
        }

        const amplifierData = pending.pendingAmplifierData;
        const programStatus = pending.pendingProgramStatus;
        const hamlib = pending.pendingHamlib;
        const auditLog = pending.pendingAuditLog;
        pending.pendingAmplifierData = null;
        pending.pendingProgramStatus = null;
        pending.pendingHamlib = null;
        pending.pendingAuditLog = null;

        subscribers.forEach((subscriber) => {
            try {
                if (amplifierData !== null) {
                    subscriber.callback(amplifierData);
                }
                if (programStatus !== null && subscriber.programStatusCallback) {
                    subscriber.programStatusCallback(programStatus);
                }
                if (hamlib !== null && subscriber.hamlibControlCallback) {
                    subscriber.hamlibControlCallback(hamlib);
                }
                if (auditLog !== null && subscriber.auditLogCallback) {
                    subscriber.auditLogCallback(auditLog);
                }
            } catch (error) {
                console.error('AmplifierMqttService: 分发设备状态回调失败:', error);
            }
        });
    }

    private scheduleFlush(deviceId: string): void {
        const pending = this.getDevicePending(deviceId);
        if (pending.throttleTimerId !== null) return;
        pending.throttleTimerId = setTimeout(() => {
            this.flushDevice(deviceId);
        }, AmplifierMqttService.STATUS_THROTTLE_MS);
    }

    private handleStatusTopic(deviceId: string, message: any): void {
        try {
            const messageString = typeof message === 'string' ? message : message.toString();
            const data = JSON.parse(messageString) as AmplifierData;
            const pending = this.getDevicePending(deviceId);
            pending.pendingAmplifierData = data;
            this.scheduleFlush(deviceId);
        } catch (error) {
            console.error('AmplifierMqttService: 解析设备状态消息失败:', error);
        }
    }

    private handleJsonStatusTopic(deviceId: string, message: any): void {
        try {
            const messageString = typeof message === 'string' ? message : message.toString();
            const jsonMessage = JSON.parse(messageString);
            const pending = this.getDevicePending(deviceId);
            if (jsonMessage.cmd === 'update_program_status') {
                pending.pendingProgramStatus = jsonMessage as ProgramStatusMessage;
                this.scheduleFlush(deviceId);
            } else if (jsonMessage.cmd === 'set_hamlib') {
                pending.pendingHamlib = jsonMessage as HamlibControlRequestMessage;
                this.scheduleFlush(deviceId);
            } else if (jsonMessage.cmd === 'update_audit_log') {
                pending.pendingAuditLog = jsonMessage as UpdateAlarmLogStatusMessage;
                this.scheduleFlush(deviceId);
            }
        } catch (error) {
            console.error('AmplifierMqttService: 解析JSON状态消息失败:', error);
        }
    }

    private ensureMessageCallbacksInstalled(): void {
        if (this.messageListenerUnsub) {
            return;
        }
        if (!mqttService.isServiceInitialized()) {
            return;
        }
        const mqttManager = mqttService.getMqttManager();
        const isDev = process.env.NODE_ENV === 'development';
        this.messageListenerUnsub = mqttManager.addMessageListener(
            (receivedTopic: string, message: string | Buffer) => {
                const match = receivedTopic.match(/^server\/device\/([^/]+)\/(status|json_status)$/);
                if (!match) {
                    return;
                }
                const [, deviceId, suffix] = match;
                const subscribers = this.subscribersByDevice.get(deviceId);
                if (!subscribers || subscribers.size === 0) {
                    return;
                }
                if (suffix === 'status') {
                    this.handleStatusTopic(deviceId, message);
                } else {
                    this.handleJsonStatusTopic(deviceId, message);
                }
                if (isDev) {
                    console.log(
                        `AmplifierMqttService: 已分发 ${receivedTopic} 给 ${subscribers.size} 个订阅者`,
                    );
                }
            },
        );
    }

    /**
     * 订阅设备状态主题
     * 订阅 server/device/{deviceId}/status 主题并处理接收到的消息
     * 接收效率：热路径仅做一次解析、按 topic 分支；生产环境减少 log。
     * 渲染效率：对回调做节流，合并短时间内的多次消息为一次下发，减少 React 重绘。
     */
    async subscribeToDeviceStatus(
        deviceId: string,
        callback: (data: AmplifierData) => void,
        programStatusCallback?: (data: ProgramStatusMessage) => void,
        hamlibControlCallback?: (data: HamlibControlRequestMessage) => void,
        auditLogCallback?: (data: UpdateAlarmLogStatusMessage) => void,
        subscriberKey?: string
    ): Promise<void> {
        const key = subscriberKey ?? `subscriber-${++this.subscriberCounter}`;
        try {
            if (!mqttService.isConnected()) {
                try {
                    await mqttService.connect();
                } catch (connectError) {
                    console.error('AmplifierMqttService: 连接MQTT服务失败:', connectError);
                    throw connectError;
                }
            }

            if (!mqttService.isServiceInitialized()) {
                console.error('AmplifierMqttService: MQTT服务未初始化，无法订阅设备状态主题');
                throw new Error('MQTT服务未初始化');
            }

            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/status`;
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            const isDev = process.env.NODE_ENV === 'development';
            const subscribers = this.subscribersByDevice.get(deviceId) ?? new Map<string, AmplifierStatusSubscriber>();
            const hadSubscribers = subscribers.size > 0;
            subscribers.set(key, { callback, programStatusCallback, hamlibControlCallback, auditLogCallback });
            this.subscribersByDevice.set(deviceId, subscribers);

            if (!hadSubscribers) {
                await mqttManager.subscribe(topic, 1);
                await mqttManager.subscribe(jsonStatusTopic, 1);
                if (isDev) {
                    console.log('AmplifierMqttService: 已订阅设备状态主题:', topic, jsonStatusTopic);
                }
            }
            this.ensureMessageCallbacksInstalled();
        } catch (error) {
            console.error('AmplifierMqttService: 订阅设备状态主题失败:', error);
            const subscribers = this.subscribersByDevice.get(deviceId);
            if (subscribers) {
                subscribers.delete(key);
                if (subscribers.size === 0) {
                    this.subscribersByDevice.delete(deviceId);
                }
            }
            throw error;
        }
    }

    /** 清除节流定时器与待下发数据，避免卸载后仍触发回调 */
    private clearThrottle(deviceId?: string): void {
        if (deviceId) {
            const pending = this.pendingByDevice.get(deviceId);
            if (!pending) return;
            if (pending.throttleTimerId !== null) {
                clearTimeout(pending.throttleTimerId);
            }
            this.pendingByDevice.delete(deviceId);
            return;
        }
        this.pendingByDevice.forEach((pending) => {
            if (pending.throttleTimerId !== null) {
                clearTimeout(pending.throttleTimerId);
            }
        });
        this.pendingByDevice.clear();
    }

    /**
     * 取消订阅设备状态主题
     */
    async unsubscribeFromDeviceStatus(deviceId: string, subscriberKey?: string): Promise<void> {
        try {
            const subscribers = this.subscribersByDevice.get(deviceId);
            if (!subscribers || subscribers.size === 0) {
                this.clearThrottle(deviceId);
                return;
            }

            if (subscriberKey) {
                subscribers.delete(subscriberKey);
            } else {
                subscribers.clear();
            }
            if (subscribers.size > 0) return;

            this.subscribersByDevice.delete(deviceId);
            this.clearThrottle(deviceId);

            if (!mqttService.isConnected()) return;
            if (!mqttService.isServiceInitialized()) return;
            const mqttManager = mqttService.getMqttManager();
            const topic = `server/device/${deviceId}/status`;
            const jsonStatusTopic = `server/device/${deviceId}/json_status`;
            await mqttManager.unsubscribe(topic);
            await mqttManager.unsubscribe(jsonStatusTopic);
            console.log('AmplifierMqttService: 已取消订阅设备状态主题:', topic, jsonStatusTopic);
        } catch (error) {
            console.error('AmplifierMqttService: 取消订阅设备状态主题失败:', error);
            // 不抛出错误，避免影响组件渲染
        }
    }

    /**
     * 销毁服务
     */
    dispose(): void {
        this.stopParamCommandInterval();
        this.clearThrottle();
        this.subscribersByDevice.clear();
        this.messageListenerUnsub?.();
        this.messageListenerUnsub = null;
    }
}

// 创建服务实例
const amplifierMqttService = new AmplifierMqttService();

// 导出服务实例
export { amplifierMqttService };
